import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { CheckCircle2, GraduationCap, RotateCcw, Save, UserPlus } from 'lucide-react';

import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { FormSection } from '@/components/ui/FormSection';
import { Select } from '@/components/ui/Select';
import { CollegeStudentFields } from '@/features/institute/admissions/components/CollegeStudentFields';
import { admissionsApi } from '@/features/institute/admissions/api/admissions.api';
import {
  EMPTY_COLLEGE_ADMISSION,
  collegeAdmissionSchema,
  collegeStudentEditSchema,
  type CollegeStudentEditValues,
} from '@/features/institute/admissions/schemas/collegeAdmission.schema';
import { CONSOLE_PANEL_SKIN } from '@/features/institute/theme';
import { toast } from '@/hooks/useToast';
import { ApiRequestError } from '@/lib/apiClient';
import { applyFieldErrors } from '@/lib/forms';
import { cn } from '@/lib/utils';
import type { Student } from '@/types/student';
import { studentsApi } from '../api/students.api';
import { studentStatusOptions } from '../constants';
import { useLiveFormErrors } from '@/hooks/useLiveFormErrors';

export interface CollegeStudentFormProps {
  /** The record being corrected, or null when one is being admitted. */
  student: Student | null;
  /** Whether this caller may change the record, or only read it. */
  canManage: boolean;
  /** Re-reads the record after a save, so the screen shows what was stored. */
  onSaved: () => void;
}

/**
 * A college student's details — taking them, and correcting them.
 *
 * **One component for both**, for the reason the school form is one component:
 * a college student is described by the same short set of fields whether they
 * are being admitted or corrected a year later, and two components describing
 * one person is how an admission comes to accept a middle name the edit rejects
 * (CLAUDE.md §3). Which job it is doing comes from `student` and nothing else.
 *
 * Still separate from the school form, though — that one asks for a class, a
 * section, three guardians and a fee record, none of which a college has.
 * Pointing a college at it left the office looking at a Placement section with
 * an empty class list and a Save button that could never succeed.
 *
 * Both jobs share one resolver, the edit schema, because it is the superset: the
 * two differ only by `status`, which a new admission carries as `ACTIVE` and
 * which `collegeAdmissionSchema` then strips on the way to the server.
 *
 * Not offered in either job: the admission number, issued once from the
 * institute's own series, and the academic year, which the session decides.
 */
export function CollegeStudentForm({ student, canManage, onSaved }: CollegeStudentFormProps) {
  const navigate = useNavigate();
  const isEdit = Boolean(student);

  const form = useForm<CollegeStudentEditValues>({
    resolver: zodResolver(collegeStudentEditSchema),
    defaultValues: student ? toCollegeForm(student) : blankCollegeForm(),
  });
  useLiveFormErrors(form);
  const {
    register,
    reset,
    formState: { errors, isSubmitting, isDirty },
  } = form;

  // Refill when the record arrives, or when a different student is opened
  // without the component being unmounted in between.
  useEffect(() => {
    if (!student) return;
    reset(toCollegeForm(student));
  }, [student, reset]);

  const onSubmit = form.handleSubmit(async (values) => {
    try {
      if (student) {
        const saved = await studentsApi.updateForCollege(
          student.uuid,
          collegeStudentEditSchema.parse(values),
        );
        toast.success(
          `${saved.fullName}'s record is updated`,
          `Admission number ${saved.admissionNumber}.`,
        );
        onSaved();
        return;
      }

      // `status` is stripped here: a new admission is always active, and the
      // create endpoint does not take one.
      const admitted = await admissionsApi.createForCollege(collegeAdmissionSchema.parse(values));
      toast.success(
        `${admitted.fullName} has been admitted`,
        `Admission number ${admitted.admissionNumber}. Their record is on the roll now.`,
      );
      reset(blankCollegeForm());
      navigate('/admin/students');
    } catch (caught) {
      if (caught instanceof ApiRequestError) {
        // Field errors stay on the fields: a toast that fades cannot be the only
        // place someone is told which box to fix. The server's messages name the
        // reason — an email already on the roll, an admission date before the
        // birth date — so they are shown verbatim.
        applyFieldErrors(form.setError, caught);
        toast.error(isEdit ? 'Could not save this record' : 'Could not admit this student', caught.message);
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }
      toast.error(
        isEdit ? 'Could not save this record' : 'Could not admit this student',
        'Something went wrong. Please try again.',
      );
    }
  });

  return (
    <fieldset disabled={!canManage} className="contents">
      <form onSubmit={onSubmit} noValidate className="flex flex-col gap-5">
        <div className={cn('flex flex-col gap-5', CONSOLE_PANEL_SKIN)}>
          {/* The one definition both jobs use. */}
          <CollegeStudentFields register={register} errors={errors} />

          {/* Standing only exists once somebody is on the roll. */}
          {isEdit && (
            <FormSection
              title="Standing"
              description="Whether this student is still studying. Suspending them is a separate action."
            >
              <Select
                label="Status"
                options={studentStatusOptions(student?.status)}
                error={errors.status?.message}
                {...register('status')}
              />
            </FormSection>
          )}
        </div>

        <p className="flex items-center gap-2 text-xs font-medium text-ink-3">
          <GraduationCap className="h-3.5 w-3.5 shrink-0" aria-hidden />
          Course and semester placement is not set up yet — students are admitted to the institute
          first and placed when it is.
        </p>

        {canManage && (
          <div className="sticky bottom-4 z-10">
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-3xl border border-slate-200/90 dark:border-slate-800 bg-white/90 dark:bg-[#111c2e]/90 px-4 py-3 shadow-[0_18px_40px_-16px_rgba(15,23,42,0.45)] backdrop-blur-xl">
              <p className="flex min-w-0 items-center gap-2.5 text-[12.5px] font-bold text-slate-600 dark:text-slate-300">
                <CheckCircle2
                  className="h-4 w-4 shrink-0 text-blue-600 dark:text-blue-400"
                  aria-hidden
                />
                {isEdit
                  ? isDirty
                    ? 'Unsaved corrections. They are recorded against your account.'
                    : 'Nothing changed yet.'
                  : 'The admission number is issued when you save.'}
              </p>

              <div className="flex flex-wrap items-center gap-2">
                <Button
                  type="button"
                  variant="ghost"
                  disabled={isSubmitting || (isEdit && !isDirty)}
                  leftIcon={<RotateCcw className="h-4 w-4" />}
                  onClick={() => reset(student ? toCollegeForm(student) : blankCollegeForm())}
                >
                  {isEdit ? 'Undo my changes' : 'Clear the form'}
                </Button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="inline-flex cursor-pointer items-center gap-2.5 rounded-full bg-gradient-to-r from-blue-600 to-indigo-600 px-7 py-2.5 text-sm font-black text-white shadow-lg shadow-indigo-500/30 hover:brightness-105 active:brightness-95 transition-[filter,transform,box-shadow] duration-150 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {isSubmitting ? (
                    <span>{isEdit ? 'Saving…' : 'Admitting…'}</span>
                  ) : (
                    <>
                      {isEdit ? (
                        <Save className="h-4 w-4 stroke-[2.5]" aria-hidden />
                      ) : (
                        <UserPlus className="h-4 w-4 stroke-[2.5]" aria-hidden />
                      )}
                      <span>{isEdit ? 'Save this record' : 'Admit student'}</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {!canManage && (
          <Alert tone="info" title="You have read-only access">
            You can see this record but not change it. Ask an administrator to grant your role
            permission to correct a student record.
          </Alert>
        )}
      </form>
    </fieldset>
  );
}

/** `null` from the API is an empty input, not the string "null". */
const text = (value: string | null | undefined): string => value ?? '';

/**
 * A blank form.
 *
 * `status` is carried even though a new admission has none — one form type
 * serves both jobs, and `ACTIVE` is both a valid value for the resolver and the
 * truth about somebody being admitted today. `collegeAdmissionSchema` strips it
 * before it reaches the server.
 */
function blankCollegeForm(): CollegeStudentEditValues {
  return { ...EMPTY_COLLEGE_ADMISSION, status: 'ACTIVE' };
}

/** Fills the college form with a stored record. */
function toCollegeForm(student: Student): CollegeStudentEditValues {
  return {
    firstName: student.firstName,
    middleName: text(student.middleName),
    lastName: student.lastName,
    email: text(student.email),
    mobile: text(student.mobile),
    dateOfBirth: text(student.dateOfBirth),
    gender: (student.gender ?? '') as CollegeStudentEditValues['gender'],
    admissionDate: student.admissionDate,
    addressLine1: text(student.addressLine1),
    city: text(student.city),
    state: text(student.state),
    status: student.status,
  };
}
